import { selectTier, type CashbackTier } from '@/api/promotions';
import type { Nivel, TierInfo } from '@/api/alianca';

export interface TierStatic {
  key: Nivel;
  label: string;
  hex: string;
  pct: number;
  min: number;
  next: number | null;
}

export const NIVEIS: TierStatic[] = [
  { key: 'vermelho', label: 'Vermelho', hex: '#EE263E', pct: 0,  min: 0,    next: 300 },
  { key: 'laranja',  label: 'Laranja',  hex: '#DE7C00', pct: 5,  min: 300,  next: 600 },
  { key: 'amarelo',  label: 'Amarelo',  hex: '#FFB92A', pct: 10, min: 600,  next: 900 },
  { key: 'verde',    label: 'Verde',    hex: '#00C072', pct: 15, min: 900,  next: 1200 },
  { key: 'azul',     label: 'Azul',     hex: '#0090E2', pct: 20, min: 1200, next: 1500 },
  { key: 'anil',     label: 'Anil',     hex: '#835DD0', pct: 25, min: 1500, next: 1800 },
  { key: 'violeta',  label: 'Violeta',  hex: '#8345BA', pct: 30, min: 1800, next: null }
];

export const NIVEL_VERSO: Record<Nivel, string> = {
  vermelho: 'A primeira fagulha. Onde toda corrente começa.',
  laranja:  'Calor inicial. Você acelerou o passo.',
  amarelo:  'Luz constante. Hábito virando ritmo.',
  verde:    'Equilíbrio. Você é da casa.',
  azul:     'Profundidade. Frequência reconhecida.',
  anil:     'Raro. Onde poucos chegam.',
  violeta:  'O topo do arco. Plenitude.'
};

export function nivelFor(kwh: number): TierStatic {
  for (let i = NIVEIS.length - 1; i >= 0; i--) {
    if (kwh >= NIVEIS[i].min) return NIVEIS[i];
  }
  return NIVEIS[0];
}

export function proxNivel(kwh: number): TierStatic | null {
  const cur = nivelFor(kwh);
  const idx = NIVEIS.indexOf(cur);
  return idx < NIVEIS.length - 1 ? NIVEIS[idx + 1] : null;
}

export function tierByKey(key: Nivel): TierStatic {
  return NIVEIS.find((t) => t.key === key) ?? NIVEIS[0];
}

/** Adapta `TierInfo` do backend pro shape estático. Cai pro estático se falhar. */
export function tierFromInfo(info: TierInfo | null | undefined): TierStatic | null {
  if (!info) return null;
  return {
    key: info.name,
    label: info.name.charAt(0).toUpperCase() + info.name.slice(1),
    hex: info.color || tierByKey(info.name).hex,
    pct: info.percent,
    min: info.min_kwh,
    next: info.max_kwh != null ? info.max_kwh + 1 : null
  };
}

export interface EffectiveCashback {
  pct: number;
  value: number;
  source: 'alianca' | 'escala';
  aliancaPct: number;
  escalaPct: number;
}

/**
 * Calcula cashback efetivo na recarga, conforme spec (Notion §8.3 + brainstorm 2026-05).
 *
 * Regra:
 * - Sempre que Aliança ativa, garante `aliancaPct` em qualquer valor.
 * - A partir de R$ 250, se o cashback escalonado (Wallet) for maior, ele assume.
 * - Cliente sem Aliança (`aliancaPct === 0`) cai no comportamento original
 *   (só Wallet — escala a partir de R$ 250 conforme regra ativa).
 *
 * Espelha `apply_bonus_for_topup` em `bow/alianca/service.py`:
 *   bonus_aplicado_pct = MAX(wallet_pct, alianca_pct)
 *   cashback = floor_money(amount * bonus_aplicado_pct / 100)
 */
export function effectiveCashback(
  amount: number,
  aliancaPct: number,
  walletTiers: CashbackTier[],
  methodFactor = 1
): EffectiveCashback {
  const escalaPct = walletPctFor(amount, walletTiers);
  const winnerPct = Math.max(aliancaPct, escalaPct);
  const source: 'alianca' | 'escala' =
    aliancaPct >= escalaPct && aliancaPct > 0 ? 'alianca' : 'escala';
  // O fator do trilho (051) incide sobre o VALOR depois do MAX — espelha
  // `apply_bonus_for_topup`: floor(amount * pct/100 * fator). Cartão no dia 1
  // tem fator 0 → cashback 0, mesmo com pct de escala/aliança > 0. Nunca paga
  // centavo a mais (floor).
  const value =
    winnerPct > 0 && methodFactor > 0
      ? Math.floor(amount * winnerPct * methodFactor) / 100
      : 0;
  return { pct: winnerPct, value, source, aliancaPct, escalaPct };
}

function walletPctFor(amount: number, tiers: CashbackTier[]): number {
  // Espelha `_select_tier` do backend (bow/promotions/service.py): vale a faixa
  // de MAIOR `min_brl <= amount` (bracket escalonado), não o maior % entre faixas.
  // Garante que o preview do app == cashback creditado, mesmo em regras fora de ordem.
  const tier = selectTier(amount, tiers);
  return tier ? tier.percent : 0;
}

/** Piso (1º nível COM cashback = Laranja) e teto (Violeta), derivados de NIVEIS. */
export const ALIANCA_FLOOR_PCT = NIVEIS.find((t) => t.pct > 0)?.pct ?? 5;
export const ALIANCA_CEIL_PCT = NIVEIS[NIVEIS.length - 1].pct;

export interface AliancaGap {
  /** O que o NÃO-membro já ganha neste valor (só escala Wallet). */
  current: EffectiveCashback;
  /** Cashback no piso garantido da Aliança (`floorPct`). */
  floorValue: number;
  /** Cashback no teto (`ceilPct`, Violeta). */
  ceilValue: number;
  /** Quanto ele deixa na mesa: floorValue − current.value (nunca negativo). */
  onTable: number;
}

/**
 * Quanto um NÃO-membro deixaria de ganhar neste valor contra o piso garantido
 * da Aliança. Alimenta o callout de captação do PIX (design alianca-pix-callout,
 * variante "No hero"). `current` reusa `effectiveCashback(amount, 0, walletTiers)`
 * — o que ele já tem pela escala. `floorValue`/`ceilValue` via floor (espelha
 * `floor_money` do backend — nunca paga centavo a mais).
 *
 * Quando `onTable <= 0` a escala já cobre o piso → o callout cai no fallback
 * honesto "cashback garantido · até 30%".
 */
export function aliancaGap(
  amount: number,
  walletTiers: CashbackTier[],
  floorPct = ALIANCA_FLOOR_PCT,
  ceilPct = ALIANCA_CEIL_PCT
): AliancaGap {
  const current = effectiveCashback(amount, 0, walletTiers);
  const floorValue = floorPct > 0 ? Math.floor(amount * floorPct) / 100 : 0;
  const ceilValue = ceilPct > 0 ? Math.floor(amount * ceilPct) / 100 : 0;
  const onTable = Math.max(0, floorValue - current.value);
  return { current, floorValue, ceilValue, onTable };
}

/**
 * "≈ N recargas" — quantas recargas no ritmo médio o cliente ainda precisa
 * pra alcançar `kwhFaltam`. Default kWh médio = 35 (próximo da realidade Bow).
 */
export function recargasParaAlcancar(kwhFaltam: number, kwhMedio = 35): number {
  if (kwhFaltam <= 0) return 0;
  return Math.max(1, Math.ceil(kwhFaltam / kwhMedio));
}
